import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { isEmptyArray, parseEmptyArray } from '@common/helpers';
import { MListData, PREVIOUS_PAGE_IDENTIFIER, ServicingFunctionIdType, ServicingPageIdType } from '@common/interfaces';
import { FrequentFlyerCards } from '@common/interfaces/frequent-flyer-cards';
import { AirportI18n } from '@common/interfaces/master/airport_i18n_all';
import { CountryI18nBy2LetterCodes } from '@common/interfaces/master/country_i18n_by2lettercode';
import { MMileageProgramI18n } from '@common/interfaces/master/m_mileage_program_i18n';
import { CountryCountryI18nAllData, LOAD_MASTERS } from '@common/master';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingPassengerService } from '@common/services/my-booking/my-booking-passenger/my-booking-passenger.service';
import { MyBookingStoreService } from '@common/services/my-booking/my-booking-store.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { SupportComponent } from '@lib/components/support-class';
import { LogType, SessionStorageName } from '@lib/interfaces';
import {
  AswMasterService,
  CommonLibService,
  LoggerDatadogService,
  PageInitService,
  PageLoadingService,
} from '@lib/services';
import { forkJoin, map, of, Subscription, take } from 'rxjs';
import {
  Bound,
  GetOrderResponseData,
  GetOrderResponseDataContactsRepresentativePhonesInner,
  Traveler,
  Type1,
} from 'src/sdk-servicing';

const MY_BOOKING_PASSENGER_MASTER_DATA_LIST = [
  LOAD_MASTERS.COUNTRY_I18N_BY_2LETTERCODE,
  LOAD_MASTERS.M_MILEAGE_PROGRAM_I18N,
  LOAD_MASTERS.AIRPORT_I18N_ALL,
  LOAD_MASTERS.LISTDATA_ALL,
  LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL,
];

type IMyBookingPassengerMasterData = [
  CountryI18nBy2LetterCodes,
  MMileageProgramI18n[],
  AirportI18n,
  MListData[],
  CountryCountryI18nAllData[]
];

@Component({
  selector: 'asw-my-booking-passenger-cont',
  templateUrl: './my-booking-passenger-cont.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingPassengerContComponent extends SupportComponent {
  // 注文応答データの取得
  orderData!: GetOrderResponseData;

  /** 予約詳細(搭乗者) */
  pageId: string = ServicingPageIdType.MYBOOKING_PASSENGER;

  /** 予約確認 */
  functionId: string = ServicingFunctionIdType.BOOKING_CONFIRMATION;
  private _subscriptions: Subscription = new Subscription();

  travelers: Traveler[] = [];
  flights: Array<Type1> = [];

  constructor(
    private readonly _common: CommonLibService,
    private readonly _pageInitService: PageInitService,
    private readonly _myBookingPassengerService: MyBookingPassengerService,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _aswMasterService: AswMasterService,
    private readonly _loggerDatadogService: LoggerDatadogService,
    private readonly _myBookingService: MyBookingService,
    private readonly _change: ChangeDetectorRef,
    private readonly _pageLoadingService: PageLoadingService,
    private readonly _myBookingStoreService: MyBookingStoreService
  ) {
    super(_common);
  }

  /** (1) 1.	画面の初期化処理(G01-003)の初期化開始処理を呼び出す。*/
  screenInit() {
    const identificationId = sessionStorage.getItem(SessionStorageName.IDENTIFICATION_ID);
    this._loggerDatadogService.info(LogType.PAGE_VIEW, {
      identificationId,
      functionId_pageId: `${this.functionId}_${this.pageId}`,
    });
  }

  onInit() {
    this._pageLoadingService.startLoading(true);
    this._subscriptions.add(
      forkJoin([
        this._aswMasterService
          .load(MY_BOOKING_PASSENGER_MASTER_DATA_LIST, true)
          .pipe(map((res) => res as IMyBookingPassengerMasterData)),
        this._servicingCommonStoreService.getServicingCommonModel$().pipe(take(1)),
        this._myBookingStoreService.getMyBookingModel$().pipe(take(1)),
      ]).subscribe((res) => {
        const [
          [countryI18nBy2LetterCode, mMileageProgramI18N, airportI18nAll, mListData, mCountryI18N],
          servicingCommon,
          myBooking,
        ] = res;

        this.orderData = servicingCommon.getOrder as GetOrderResponseData;
        this.travelers = parseEmptyArray(this.orderData?.travelers as Traveler[]);
        this.flights = parseEmptyArray(
          this.orderData?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[])
        );

        this.screenInit();

        // (1) 2. 画面情報に以下の内容を設定する。
        this._common.aswCommonStoreService.updateAswCommon({
          functionId: this.functionId,
          pageId: this.pageId,
          subFunctionId: '',
          subPageId: '',
          isEnabledLogin: false,
          isUpgrade: false,
        });

        this._myBookingPassengerService.updateMasterDataPassenger({
          mCountry: this.getCountryNamesFor2And3Letter(countryI18nBy2LetterCode, mCountryI18N)
            .countryI18nBy2LetterCodesFiltered,
          mMileageProgramI18N: this.getFrequentFlyerProgramNames(parseEmptyArray(mMileageProgramI18N)),
          airportI18nAll: airportI18nAll,
          mListData: this.getGeneralMasterList(parseEmptyArray(mListData)),
          mCountryI18N: parseEmptyArray(
            this.getCountryNamesFor2And3Letter(countryI18nBy2LetterCode, mCountryI18N).countryI18nBy3LetterCodesFiltered
          ),
        });

        this._change.markForCheck();
        this._pageInitService.endInit(
          of({
            getOrderReply: {
              data: {
                ...this.orderData,
                travelerSupports: myBooking?.travelerSupports, // Set data travelerSupports of get-order unmasked with message dynamic for S01-P032
              },
            },
            aswContext: this._common.aswContextStoreService.aswContextData,
          })
        );
        this._pageLoadingService.endLoading(true);
      })
    );
  }

  reload(): void {}
  init(): void {
    this._subscriptions.add(
      this._myBookingService.onInitFinishedSubject.subscribe((initFinished) => {
        if (initFinished) {
          this.onInit();
        }
      })
    );
  }

  destroy(): void {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.MYBOOKING_PASSENGER,
    });
    this._subscriptions.unsubscribe();
  }

  /** (1) 3. 以下の内容でASWDB(マスタ)より情報を取得する。*/
  /** (1) 3.1. 以下の通りに電話番号種別リストを取得する。 */
  getGeneralMasterList(mListData: MListData[]) {
    // 3.1. 以下の通りに電話番号種別リストを取得する。
    // 3.1.1. 空の電話番号種別コードリストを作成する
    const phoneTypes: string[] = [];
    // <以下、PNR情報取得API応答.data.contacts.representative.phones[]の要素数繰り返し>(それぞれの要素が当該代表者電話番号)
    const phones = parseEmptyArray(this.orderData?.contacts?.representative?.phones);
    // 3.1.2. 電話番号種別コードリストに当該代表者電話番号.typeを追加する。
    phones.forEach((phone: GetOrderResponseDataContactsRepresentativePhonesInner) => {
      phone?.type && phoneTypes.push(phone.type);
    });
    // 3.1.3. 以下の内容でASWDB(マスタ)より電話番号種別リストを取得する。
    return mListData.filter(
      (item: MListData) =>
        item?.data_code === 'PD_010' &&
        item?.lang === this._common?.aswContextStoreService?.aswContextData?.lang &&
        phoneTypes.includes(item?.value)
    );
  }

  /** (1) 3.2. 以下の通りにマイレージプログラム名称リストを取得する。 */
  getFrequentFlyerProgramNames(mMileageProgramI18N: MMileageProgramI18n[]) {
    // 3.2.1. 空のキャリアコードリストを作成する
    const carrierCodes: string[] = [];

    this.travelers.forEach((traveler) => {
      // <以下、PNR情報取得API応答.data.frequentFlyerCards.<travelerId>要素数繰り返し>(それぞれの要素が該当マイレージプログラム情報)
      // <以下、PNR情報取得API応答.data.frequentFlyerCards.<travelerId>.fqtr[]の要素数繰り返し>(それぞれの要素が該当マイレージプログラム情報のfqtr)
      const fqtrList = parseEmptyArray(
        (this.orderData?.frequentFlyerCards as FrequentFlyerCards)?.[traveler?.id!]?.fqtr
      );
      // 3.2.2.キャリアコードリストに 該当マイレージプログラム情報のfqtr.companyCodeを追加する
      fqtrList.forEach((fqtr) => {
        fqtr?.companyCode && carrierCodes.push(fqtr.companyCode);
      });

      // <以下、PNR情報取得API応答.data.frequentFlyerCards.<travelerId>要素数繰り返し>(それぞれの要素が該当マイレージプログラム情報)
      // <以下、PNR情報取得API応答.data.frequentFlyerCards.<travelerId>.fqtv[]の要素数繰り返し>(それぞれの要素が該当マイレージプログラム情報のfqtv)
      const fqtvList = parseEmptyArray(
        (this.orderData?.frequentFlyerCards as FrequentFlyerCards)?.[traveler?.id!]?.fqtv
      );
      // 3.2.3.キャリアコードリストに 該当マイレージプログラム情報のfqtv.companyCodeを追加する。
      fqtvList.forEach((fqtv) => {
        fqtv?.companyCode && carrierCodes.push(fqtv.companyCode);
      });
    });

    // 3.2.4. 以下の内容でASWDB(マスタ)よりマイレージプログラム名称リストを取得する。
    return mMileageProgramI18N.filter(
      (item: MMileageProgramI18n) =>
        item?.lang === this._common?.aswContextStoreService?.aswContextData?.lang &&
        carrierCodes.includes(item?.carrier_code)
    );
  }

  // (1) 3.3.	以下の内容でASWDB(マスタ)よりSafe Travelウィジェット機能有効フラグを取得する。
  // (1) 3.5. 以下の通りに国名称を取得する。
  getCountryNamesFor2And3Letter(mCountry: CountryI18nBy2LetterCodes, mCountryI18N: CountryCountryI18nAllData[]) {
    // 3.5.1. 空の国コード2レターリストを作成する。
    const country2LetterCodes: string[] = [];
    // 3.5.2. 空の国コード３レターリストを作成する。
    const country3LetterCodes: string[] = [];

    // <以下、PNR情報取得API応答.data.travelers[]の要素数繰り返し> (それぞれの要素が当該搭乗者)
    this.travelers.forEach((traveler) => {
      // <以下、traveler.regulatoryDetails.visas[]の要素数繰り返し> (それぞれの要素が当該visa情報)
      const visas = parseEmptyArray(traveler?.regulatoryDetails?.visas);
      visas.forEach((visa) => {
        // 3.5.3. visa.issuanceCountryCodeが 2レターの場合, 国コード2レターリストにvisa.issuanceCountryCodeを追加する。
        visa?.issuanceCountryCode &&
          visa.issuanceCountryCode.length === 2 &&
          !country2LetterCodes.includes(visa.issuanceCountryCode) &&
          country2LetterCodes.push(visa.issuanceCountryCode);
        // 3.5.4. visa.issuanceCountryCodeが 3レターの場合, 国コード3レターリストにvisa.issuanceCountryCodeを追加する。
        visa?.issuanceCountryCode &&
          visa.issuanceCountryCode.length === 3 &&
          !country3LetterCodes.includes(visa.issuanceCountryCode) &&
          country3LetterCodes.push(visa.issuanceCountryCode);
      });
    });
    // 3.5.5. 国コード2レターリストは空ではない場合、以下の処理を行う。
    // 3.5.5.1. 以下の内容でASWDB(マスタ)より国コード(3レター)を取得する。
    const countryI18nBy2LetterCodesFiltered_1: CountryI18nBy2LetterCodes = {};

    country2LetterCodes.forEach((code) => {
      if (!isEmptyArray(mCountry[code])) {
        countryI18nBy2LetterCodesFiltered_1[code] = mCountry[code];
      }
    });

    // 3.5.5.2. 以下の内容でASWDB(マスタ)より国名称を取得する。
    const countryI18nBy3LetterCodesFiltered_1 = Object.values(countryI18nBy2LetterCodesFiltered_1)
      .map((_value) => mCountryI18N.find((_item) => _item.country_3letter_code === _value[0]?.country_3letter_code))
      .filter((country) => !!country);

    // 3.5.6. 国コード3レターリストは空ではない場合、「3.5.5.2」の処理を行う。
    const countryI18nBy3LetterCodesFiltered_2 = country3LetterCodes
      .map((code) => mCountryI18N.find((_item) => _item.country_3letter_code === code))
      .filter((country) => !!country);

    // 3.6. 	以下の通りに国コード3レターを取得する。
    // 3.6.1. 空の国コードリストを作成する
    const country2LetterCodes_2: string[] = [];
    // <以下、PNR情報取得API応答.data.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント)
    this.flights.forEach((flight) => {
      // 3.6.2. 国コードリストにflight.arrival.country2letterCodeを追加する
      flight?.arrival?.country2letterCode &&
        !country2LetterCodes.includes(flight.arrival.country2letterCode) &&
        country2LetterCodes_2.push(flight.arrival.country2letterCode);
    });

    // 3.6.3. 「3.5.5.1」の処理を行う。
    const countryI18nBy2LetterCodesFiltered_2: CountryI18nBy2LetterCodes = {};

    country2LetterCodes_2.forEach((code) => {
      if (!isEmptyArray(mCountry[code])) {
        countryI18nBy2LetterCodesFiltered_2[code] = mCountry[code];
      }
    });

    const countryI18nBy3LetterCodesFiltered_3 = Object.values(countryI18nBy2LetterCodesFiltered_2)
      .map((_value) => mCountryI18N.find((_item) => _item.country_3letter_code === _value[0]?.country_3letter_code))
      .filter((country) => !!country);

    return {
      countryI18nBy2LetterCodesFiltered: {
        ...countryI18nBy2LetterCodesFiltered_1,
        ...countryI18nBy2LetterCodesFiltered_2,
      },
      countryI18nBy3LetterCodesFiltered: [
        ...countryI18nBy3LetterCodesFiltered_1,
        ...countryI18nBy3LetterCodesFiltered_2,
        ...countryI18nBy3LetterCodesFiltered_3,
      ] as CountryCountryI18nAllData[],
    };
  }
}
